import React, { useEffect, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Trend arrow based on current vs previous value
const trend = (current, previous) => {
  if (previous == null || previous === 0 || current === previous) return 'flat';
  return current > previous ? 'up' : 'down';
};

const TrendArrow = ({ current, previous }) => {
  const t = trend(current, previous);
  if (t === 'up') return <i className="fas fa-caret-up text-emerald-400"></i>;
  if (t === 'down') return <i className="fas fa-caret-down text-red-400"></i>;
  return <i className="fas fa-minus text-gray-500 text-[10px]"></i>;
};

const Item = ({ c }) => {
  const currentRate = c.rate || c.sell || c.buy || 0;
  const prevRate = c.prev_rate != null ? c.prev_rate : (c.prev_sell != null ? c.prev_sell : c.prev_buy);
  const t = trend(currentRate, prevRate);
  const hasRate = currentRate > 0;

  return (
    <div className="inline-flex items-center gap-3 px-6 py-2.5 border-r border-white/10">
      <span className="text-2xl leading-none">{c.flag}</span>
      <div>
        <p className="text-xs uppercase tracking-widest text-white/60 font-bold leading-none">
          EUR → {c.code}
        </p>
        {hasRate ? (
          <div className="flex items-center gap-2 mt-1">
            <span className="flex items-center gap-1 text-white font-mono font-bold text-sm">
              {currentRate.toFixed(2)}
              <TrendArrow current={currentRate} previous={prevRate} />
            </span>
          </div>
        ) : (
          <p className="text-xs text-white/40 italic mt-0.5">Rate not set</p>
        )}
      </div>
      {/* Small trend indicator badge */}
      {hasRate && t === 'up' && (
        <span className="text-[9px] uppercase tracking-widest text-emerald-300 font-bold">▲</span>
      )}
      {hasRate && t === 'down' && (
        <span className="text-[9px] uppercase tracking-widest text-red-300 font-bold">▼</span>
      )}
    </div>
  );
};

const CurrencyTicker = () => {
  const [rates, setRates] = useState([]);
  const [partnerIdx, setPartnerIdx] = useState(0);
  const PARTNERS = ['Western Union', 'Ria', 'Fidur'];

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/currencies`);
        if (!cancelled) setRates(res.data || []);
      } catch (err) {
        // silent — ticker just won't show
      }
    };
    load();
    const id = setInterval(load, 60000); // refresh every minute
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setPartnerIdx((i) => (i + 1) % PARTNERS.length), 2200);
    return () => clearInterval(id);
  }, [PARTNERS.length]);

  if (!rates || rates.length === 0) return null;

  // Duplicate the array so the marquee has a seamless loop
  const doubled = [...rates, ...rates];

  return (
    <div className="relative bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950 border-y border-white/10 overflow-hidden py-1" data-testid="currency-ticker">
      {/* Left label — rotates through the 3 money-transfer partners */}
      <div className="absolute left-0 top-0 bottom-0 z-10 bg-gradient-to-r from-orange-500 to-red-500 px-4 flex items-center gap-2 shadow-lg min-w-[170px]" data-testid="ticker-partner-label">
        <i className="fas fa-money-bill-wave text-white text-sm"></i>
        <span
          key={partnerIdx}
          className="text-white text-xs font-black uppercase tracking-widest whitespace-nowrap"
          style={{ animation: 'partnerFade 0.5s ease-out' }}
        >
          {PARTNERS[partnerIdx]}
        </span>
      </div>
      {/* Fade edges */}
      <div className="absolute left-40 top-0 bottom-0 w-16 bg-gradient-to-r from-slate-950 to-transparent z-10 pointer-events-none"></div>
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-slate-950 to-transparent z-10 pointer-events-none"></div>

      <div className="pl-44">
        <div className="ticker-track flex whitespace-nowrap">
          {doubled.map((c, i) => (
            <Item key={`${c.code}-${i}`} c={c} />
          ))}
        </div>
      </div>

      <style>{`
        .ticker-track {
          animation: ticker-scroll 15s linear infinite;
          will-change: transform;
        }
        .ticker-track:hover {
          animation-play-state: paused;
        }
        @keyframes ticker-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        @keyframes partnerFade {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CurrencyTicker;
